import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  organizationSeedsItemsSelector,
  organizationSeedsRemoteTotalSelector,
  isLoadingOrganizationSeedsSelector,
  getOrganizationSeedInventoriesListAction,
  cleanOrganizationSeedsAction,
} from 'state/slices/organizations';
import { usePagination } from 'hooks/pagination';
import {
  Organization,
  PaginationData,
  SeedInventorySummary,
  Variety,
} from 'constants/interface';
import { Search, Table } from 'UI';

import {
  SearchContainer,
} from './style';

interface InventoryViewProps {
  shouldReload: boolean
  onReloadFinish(): void
  activeOrganization: Organization
}

const InventoryView = ({
  shouldReload,
  onReloadFinish,
  activeOrganization,
}: InventoryViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(organizationSeedsRemoteTotalSelector);

  const inventoryItems = useSelector(organizationSeedsItemsSelector);
  const isRemoteLoading = useSelector(isLoadingOrganizationSeedsSelector);

  const inventories = inventoryItems.map((inventory) => {
    const tableInventory: SeedInventorySummary & {
      key?: string,
    } = {
      ...inventory,
      key: `${inventory.variety.id.toString()}/${inventory.owner.id.toString()}`,
    };
    return tableInventory;
  }).filter((tableInventory): tableInventory is SeedInventorySummary => !!tableInventory);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getOrganizationSeedInventoriesListAction({
      ...paginationData,
      organization: activeOrganization.id,
    }));
  };
  const pagination = usePagination<SeedInventorySummary>(
    remoteFetch,
    {
      filter: {
        variety: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanOrganizationSeedsAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar no estoque" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={inventories}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Cultura"
          key="culture"
          dataIndex="variety"
          render={(variety: Variety) => `${variety.culture.name}`}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'culture' ? pageData.order : undefined}
        />
        <Table.Column
          title="Variedade"
          key="variety"
          dataIndex="variety"
          render={(variety: Variety) => `${variety.name}`}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'variety' ? pageData.order : undefined}
        />
        <Table.Column
          title="Saldo atual"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} sementes`;
            return content;
          }}
        />
        <Table.Column
          title="Custo médio"
          key="averageCost"
          dataIndex="averageCost"
          render={(averageCost: number) => {
            const currencyValue = Intl
              .NumberFormat(
                'pt-BR',
                {
                  style: 'currency',
                  currency: 'BRL',
                },
              ).format((averageCost / 100));
            return currencyValue;
          }}
        />
      </Table>
    </>
  );
};

export default InventoryView;
